import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ExternalLink, AlertTriangle, RefreshCw } from "lucide-react";
import useBugTicket from "./useBugTicket";
import { formatBotMessage } from "../dashboard5/utils/formatMessage";

// Shared utility strings, kept in the palette the other pages use
// (#FAFAF6 ground, cyan accent, gray borders, #253439 headings).
export const BTN =
  "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] rounded-md border border-gray-200 bg-white text-[#253439] hover:bg-cyan-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-1";
export const CARD = "bg-white border border-gray-200 rounded-lg shadow-sm p-4";
export const LINK = "text-cyan-700 hover:underline";

// formatBotMessage's classes (.d3-link, .d3-code-block, ...) read these
// variables, which only exist under .d3-root (a full-height flex shell we do
// not want here). Same values as dashboard3.css.
const READER_VARS = {
  "--arta-primary": "#06b6d4",
  "--arta-primary-dark": "#0891b2",
  "--arta-primary-light": "rgba(6, 182, 212, 0.08)",
  "--arta-surface": "#f9fafb",
  "--arta-text": "#1a1d26",
  "--arta-border": "#e2e5eb",
  "--arta-border-subtle": "#eceef2",
  "--arta-radius-sm": "6px",
};

// ── Jira wiki markup -> the markdown formatBotMessage reads ─────────────────
const FENCE_RE = /```[^\n]*\n([\s\S]*?)```|\{(?:code|noformat)(?::[^}]*)?\}([\s\S]*?)\{(?:code|noformat)\}/gi;
const LOG_LINE_RE =
  /^\s*(?:#\d+\s+0x[0-9a-f]+|\d+:\s+\/|\/(?:lib|usr|opt|var|bin|home)\S*|at\s+[\w$.<>]+[(\s]|\[0x[0-9a-f]+\]|\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2})/i;

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Prose only. Code is never escaped here: the reader escapes code itself.
const proseToMarkdown = (raw) => {
  let t = escapeHtml(raw)
    .replace(/[-]/g, "") // reserved for the markers below; never from ticket text
    .replace(/!image-[^!\n]*!/g, "")
    .replace(/\{_\}\*([^*\n]+)\*\{_\}:/g, "\n\n**$1:**")
    // {_}italic{_} and the other Jira toggles go before the _x_ rule below,
    // which would otherwise pair up their underscores and leave "{}" behind.
    .replace(/\{_\}([^{}\n]*?)\{_\}/g, "$1")
    .replace(/\{_\}([^{}\n]*?)\{_\}/g, "$1")
    .replace(/\{_\}/g, "")
    .replace(/\{\*\}([^{}\n]*?)\{\*\}/g, "**$1**")
    .replace(/\{\*\}/g, "")
    .replace(/\{color(?::[^}]*)?\}|\{quote\}|\{panel(?::[^}]*)?\}/g, "")
    // "++ text" is a sub-bullet, not literal pluses.
    .replace(/^[ \t]*\+{2,}[ \t]+/gm, "- ")
    // bold wrapped around underline (*+x+*) -> underline only
    .replace(/\*\+(?=[^\s+])([^+\n]*?[^\s+])\+\*/g, "+$1+")
    // +underline+ (a section label in practice) -> bold, with any stray quote
    // hugging it and any bold/italic marks nested inside it (+*x*+). Needs word
    // edges, so "+1 555" and "C++" are left alone.
    .replace(
      /(^|[\s(])['’]?\+(?=[^\s+])([^+\n]*?[^\s+])\+['’]?(?=$|[\s.,:;)!?])/gm,
      (_m, pre, inner) => `${pre}**${inner.replace(/^[*_]+|[*_]+$/g, "")}**`
    )
    // a "+" left on a label line: "+Environment:" / "Environment:+"
    .replace(/^([ \t]*)\+(?=[^\s+][^\n]*:[ \t]*$)/gm, "$1")
    .replace(/:\+[ \t]*$/gm, ":")
    // -strike-, ^super^, ~sub~: the reader cannot draw these, so mark them with
    // private-use characters and swap in <s>/<sup>/<sub> after it has run.
    // Word edges and short inner text keep "- item", "well-known", "-v" and
    // "~2h of traffic" out of it.
    .replace(/(^|[\s(])-(?=[^\s-])([^-\n]*?[^\s-])-(?=$|[\s.,:;)!?])/gm, "$1$2")
    .replace(/\^([\w.+-]{1,12})\^/g, "$1")
    .replace(/~([\w.+-]{1,12})~/g, "$1")
    .replace(/^[ \t]*[_*{}+]+[ \t]*$/gm, "")
    .replace(/^[ \t]*h[1-4]\.[ \t]+(.*)$/gm, "**$1**")
    .replace(/^[ \t]*bq\.[ \t]+/gm, "")
    .replace(/^[ \t]*\*[ \t]+/gm, "- ")
    .replace(/\[([^|\]\n]+)\|(https?:\/\/[^\]\s]+)\]/g, "[$1]($2)")
    .replace(/\[(https?:\/\/[^\]\s]+)\]/g, "$1")
    .replace(/\{\{([^}\n]+)\}\}/g, "`$1`")
    .replace(/(?<![\w*])_\*([^*\n]+?)\*_(?![\w*])/g, "**$1**")
    .replace(/(?<![\w*])\*(?=\S)([^*\n]*?\S)\*(?![\w*])/g, "**$1**")
    .replace(/(?<![\w_])_(?=\S)([^_\n]*?\S)_(?![\w_])/g, "$1");
  // The reader drops chat-only "Note:" / "TL;DR" lines. In a ticket they are
  // content, so hide them from that filter behind a zero-width character.
  t = t.replace(/^([ \t]*)(\*\*)?(Note\s*[-:]|TL;?DR)/gim, "$1​$2$3");
  return t;
};

// Runs of stack-trace / log lines become fenced code, the rest stays prose.
const splitLogs = (chunk) => {
  const out = [];
  let prose = [];
  let logs = [];
  const flushProse = () => {
    if (prose.length) out.push(proseToMarkdown(prose.join("\n")));
    prose = [];
  };
  const flushLogs = () => {
    if (logs.length > 1 || (logs.length === 1 && /^\s*#\d+\s+0x/.test(logs[0]))) {
      flushProse();
      out.push("```\n" + logs.join("\n") + "\n```");
    } else {
      prose.push(...logs);
    }
    logs = [];
  };
  chunk.split("\n").forEach((line) => {
    if (LOG_LINE_RE.test(line)) logs.push(line);
    else {
      flushLogs();
      prose.push(line);
    }
  });
  flushLogs();
  flushProse();
  return out;
};

const jiraToMarkdown = (text) => {
  const parts = [];
  let last = 0;
  let m;
  FENCE_RE.lastIndex = 0;
  while ((m = FENCE_RE.exec(text))) {
    parts.push(...splitLogs(text.slice(last, m.index)));
    const code = (m[1] ?? m[2] ?? "").replace(/^\n+|\s+$/g, "");
    if (code) parts.push("```\n" + code + "\n```");
    last = m.index + m[0].length;
  }
  parts.push(...splitLogs(text.slice(last)));
  // the reader turns every newline into a line break, so keep at most one blank line
  return parts.join("\n\n").replace(/\n(?:[ \t]*\n){2,}/g, "\n\n").trim();
};

export const Reader = ({ text, id }) => {
  const html = formatBotMessage(jiraToMarkdown(text || ""))
    .replace(//g, "<s>")
    .replace(//g, "</s>")
    .replace(//g, "<sup>")
    .replace(//g, "</sup>")
    .replace(//g, "<sub>")
    .replace(//g, "</sub>");
  if (!html.trim()) return <p className="text-sm text-gray-500">No description available.</p>;
  return (
    <div
      id={id}
      className="max-w-[78ch] text-sm leading-6 text-[#253439] break-words [&_.d3-code-block]:leading-5 [&_.d3-code-block]:my-2"
      style={READER_VARS}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const Description = ({ text }) => <Reader id="bt-description" text={text} />;

export const Section = ({ title, children }) => (
  <section className={CARD}>
    <h2 className="text-sm font-semibold text-[#253439] mb-3">{title}</h2>
    {children}
  </section>
);

export const toneFor = (status = "") => {
  const s = status.toLowerCase();
  if (/(closed|resolved|done|fixed)/.test(s)) return "border-green-500 text-green-700 bg-green-50";
  if (/(open|progress|mitigat|monitor|review)/.test(s)) return "border-amber-500 text-amber-700 bg-amber-50";
  return "border-gray-200 text-gray-600 bg-gray-50";
};

export const BADGE = "bt-badge text-xs font-semibold px-2 py-0.5 rounded border";
export const NEUTRAL = "border-gray-200 text-gray-600 bg-gray-50";

export const fmtDate = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

export const Row = ({ label, children }) =>
  children ? (
    <div className="grid grid-cols-[96px_1fr] gap-2 py-2 text-[13px] border-b border-gray-100 last:border-b-0">
      <dt className="text-gray-500">{label}</dt>
      <dd className="m-0 break-words text-[#253439]">{children}</dd>
    </div>
  ) : null;

export const MatchRing = ({ pct }) => (
  <div
    role="img"
    aria-label={`Match ${pct} percent`}
    className="w-11 h-11 rounded-full grid place-items-center"
    style={{ background: `conic-gradient(#06b6d4 ${pct}%, #e5e7eb 0)` }}
  >
    <span className="w-9 h-9 rounded-full bg-white grid place-items-center text-[11px] font-semibold text-[#253439]">
      {pct}%
    </span>
  </div>
);

export const Skeleton = () => (
  <div
    className="max-w-[1280px] w-full mx-auto px-6 py-5 grid gap-4 lg:grid-cols-[2fr_1fr]"
    aria-busy="true"
    aria-label="Loading ticket"
  >
    <div>
      <div className="h-7 w-3/5 rounded-lg bg-gray-200 animate-pulse" />
      <div className="h-56 mt-5 rounded-lg bg-gray-200 animate-pulse" />
    </div>
    <div className="h-80 rounded-lg bg-gray-200 animate-pulse" />
  </div>
);

// Common spinner (same RefreshCw + animate-spin the admin pages use), centred
// where the content will appear. The header stays put, so nothing shifts.
export const Loader = ({ label = "Loading…" }) => (
  <div
    className="flex flex-col items-center justify-center gap-3 min-h-[50vh] text-[13px] text-gray-500"
    role="status"
    aria-live="polite"
  >
    <RefreshCw size={28} className="animate-spin text-cyan-500" aria-hidden="true" />
    <span>{label}</span>
  </div>
);

export const Message = ({ title, text, action }) => (
  <div className="max-w-md mx-auto mt-20 px-6 text-center" role="alert">
    <h1 className="text-2xl font-bold text-[#253439] mb-2">{title}</h1>
    <p className="text-sm text-gray-500 mb-4">{text}</p>
    {action}
  </div>
);

export default function BugTicketPage() {
  const { issueKey: rawKey } = useParams();
  const issueKey = rawKey || "";
  const [params] = useSearchParams();
  const { data, loading, error, notFound, retry } = useBugTicket(issueKey);

  const match = Number(params.get("match"));
  const hasMatch = params.has("match") && Number.isFinite(match);

  return (
    <div className="w-full min-h-screen self-stretch bg-[#FAFAF6] text-[#253439]">
      <header className="flex items-center justify-between h-12 px-6 bg-white border-b border-gray-200">
        <img src="/A_bgless.png" alt="Arta" className="h-7" />
      </header>

      {loading && <Loader label="Loading ticket…" />}
      {notFound && <Message title="Ticket not found" text={`No bug ticket exists for “${issueKey}”.`} />}
      {error && (
        <Message
          title="Couldn’t load ticket"
          text={error}
          action={
            <button type="button" className={BTN} onClick={retry}>
              Retry
            </button>
          }
        />
      )}

      {data && (
        <main className="max-w-[1280px] mx-auto px-6 pt-5 pb-12">
          <nav aria-label="Breadcrumb" className="text-[13px] text-gray-500 mb-3">
            <Link to="/TseDashboard3/new" className={LINK}>
              Bugs
            </Link>
            {data.product && <> / <span>{data.product}</span></>} / <span>{data.issueKey}</span>
          </nav>

          <div className="flex flex-wrap items-start justify-between gap-6 mb-4">
            <div>
              <p className="text-[13px] font-semibold text-gray-500 mb-1">{data.issueKey}</p>
              <h1 className="text-2xl font-bold leading-snug mb-3">{data.title || `Bug ${data.issueKey}`}</h1>
              <div className="flex flex-wrap items-center gap-2">
                {data.status && <span className={`${BADGE} ${toneFor(data.status)}`}>{data.status}</span>}
                {data.priority && <span className={`${BADGE} ${NEUTRAL}`}>{data.priority}</span>}
                {data.product && <span className={`${BADGE} border-cyan-200 text-cyan-700 bg-cyan-50`}>{data.product}</span>}
                {data.namespace && <span className={`${BADGE} ${NEUTRAL}`}>{data.namespace}</span>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {hasMatch && <MatchRing pct={Math.max(0, Math.min(100, Math.round(match)))} />}
              {data.url && !/\/jira\/[^/?#]+\/?$/.test(data.url) && (
                <a className={BTN} href={data.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={13} /> Open in Jira
                </a>
              )}
            </div>
          </div>

          {data.warnings?.length > 0 && (
            <div className="flex gap-2 px-3 py-2.5 mb-4 text-[13px] rounded-md border border-amber-500 bg-amber-50 text-amber-800" role="status">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <ul className="m-0 p-0 list-none">
                {data.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {(
            <div className="grid gap-4 items-start lg:grid-cols-[2fr_1fr]">
              <div className="flex flex-col gap-4 min-w-0">
                <Section title="Description">
                  <Description text={data.description} />
                </Section>
                {data.resolution && (
                  <Section title="Resolution">
                    <Reader text={data.resolution} />
                  </Section>
                )}
              </div>
              <aside aria-label="Details" className="order-last">
                <section className={CARD}>
                  <h2 className="text-sm font-semibold mb-3">Details</h2>
                  <dl className="m-0">
                    <Row label="Status">{data.status}</Row>
                    <Row label="Priority">{data.priority}</Row>
                    <Row label="Product">{data.product}</Row>
                    <Row label="Namespace">{data.namespace}</Row>
                    <Row label="Issue Key">{data.issueKey}</Row>
                    <Row label="Issue ID">{data.issueId}</Row>
                    <Row label="Components">
                      {Array.isArray(data.components) ? data.components.join(", ") : data.components}
                    </Row>
                    <Row label="Assignee">{data.assignee}</Row>
                    <Row label="Reporter">{data.reporter}</Row>
                    <Row label="Created">{data.createdAt && fmtDate(data.createdAt)}</Row>
                    <Row label="Updated">{data.updatedAt && fmtDate(data.updatedAt)}</Row>
                    <Row label="Chunks">{data.chunkCount > 0 && `${data.chunksFound} / ${data.chunkCount}`}</Row>
                  </dl>
                </section>
              </aside>
            </div>
          )}
        </main>
      )}
    </div>
  );
}
